/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import {
  GET_USER_PORTFOLIOS,
  GET_USER_PORTFOLIOS_NEXT,
  GET_USER_PORTFOLIOS_PREV,
} from "graphql/query/admin";
import Table from "Components/Table/Table";

const VISIBILITY_OPTIONS = ["PUBLIC", "PRIVATE"];

const PortfolioTab = ({ userId }) => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [pageInfo, setPageInfo] = useState("");
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ isActiveBool: null, visibility: null });
  const [appliedFilters, setAppliedFilters] = useState({});

  const buildFilters = () => {
    const f = {};
    if (appliedFilters.isActiveBool !== null && appliedFilters.isActiveBool !== undefined) {
      f.isActiveBool = appliedFilters.isActiveBool;
    }
    if (appliedFilters.visibility) f.visibility = appliedFilters.visibility;
    return f;
  };

  const { data, refetch, error, loading } = useQuery(GET_USER_PORTFOLIOS, {
    variables: {
      userId,
      first: 10,
      orderBy: "createdAt_DESC",
      filters: buildFilters(),
    },
    fetchPolicy: "network-only",
    skip: !userId,
  });

  const [getNextPage, { loading: nextLoader }] = useLazyQuery(GET_USER_PORTFOLIOS_NEXT);
  const [getPrevPage, { loading: prevLoader }] = useLazyQuery(GET_USER_PORTFOLIOS_PREV);

  const mapEdges = (edges) =>
    edges.map((e) => ({
      ...e.node,
      tagsDisplay: Array.isArray(e.node.tags) ? e.node.tags.join(", ") : e.node.tags || "N/A",
    }));

  const handleNextPage = async () => {
    if (pageInfo.hasNextPage) {
      const res = await getNextPage({
        variables: { userId, first: 10, after: pageInfo.endCursor, orderBy: "createdAt_DESC", filters: buildFilters() },
        fetchPolicy: "network-only",
      });
      if (res.data?.userPortfolios) {
        setPortfolios(mapEdges(res.data.userPortfolios.edges));
        setPage(page + 1);
        setPageInfo(res.data.userPortfolios.pageInfo);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (pageInfo.hasPreviousPage) {
      const res = await getPrevPage({
        variables: { userId, last: 10, before: pageInfo.startCursor, orderBy: "createdAt_DESC", filters: buildFilters() },
        fetchPolicy: "network-only",
      });
      if (res.data?.userPortfolios) {
        setPortfolios(mapEdges(res.data.userPortfolios.edges));
        setPage(page - 1);
        setPageInfo(res.data.userPortfolios.pageInfo);
      }
    }
  };

  useEffect(() => {
    if (data?.userPortfolios) {
      setPortfolios(mapEdges(data.userPortfolios.edges));
      setPageInfo(data.userPortfolios.pageInfo);
    }
    setPage(0);
  }, [data]);

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filters });
    setTimeout(() => refetch(), 100);
  };

  const handleResetFilter = () => {
    setFilters({ isActiveBool: null, visibility: null });
    setAppliedFilters({});
    setTimeout(() => refetch(), 100);
  };

  const handleViewButton = (id) => {
    const portfolio = portfolios.find((p) => p.id === id);
    navigate("/admin/user/view-portfolio", { state: { portfolioId: id, userId, portfolio } });
  };

  const TABLE_HEAD = [
    { id: "id", flag: "id", label: "No.", alignRight: false },
    { id: "title", flag: "title", label: "Title", alignRight: false },
    { id: "visibility", flag: "visibility", label: "Visibility", alignRight: false },
    { id: "tagsDisplay", flag: "tagsDisplay", label: "Tags", alignRight: false },
    { id: "likeCount", flag: "likeCount", label: "Likes", alignRight: false },
    { id: "commentCount", flag: "commentCount", label: "Comments", alignRight: false },
    { id: "isActive", flag: "isActive", label: "Active", alignRight: false },
    { id: "createdAt", flag: "createdAt", label: "Created At", alignRight: false },
    {
      id: "actions",
      flag: { read: true },
      callback: { handleViewButton },
      label: "Actions",
      alignRight: false,
    },
  ];

  return (
    <Box style={{ padding: "16px 0" }}>
      {/* Filters */}
      <Grid container spacing={2} style={{ marginBottom: 16, padding: "0 8px" }}>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Visibility</InputLabel>
            <Select
              label="Visibility"
              value={filters.visibility || ""}
              onChange={(e) => setFilters({ ...filters, visibility: e.target.value || null })}
            >
              <MenuItem value="">All</MenuItem>
              {VISIBILITY_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Active</InputLabel>
            <Select
              label="Active"
              value={filters.isActiveBool === null ? "" : String(filters.isActiveBool)}
              onChange={(e) =>
                setFilters({ ...filters, isActiveBool: e.target.value === "" ? null : e.target.value === "true" })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} style={{ display: "flex", gap: 8 }}>
          <Button variant="contained" color="primary" onClick={handleApplyFilter} style={{ background: "var(--color-primary, #1976d2)" }}>
            Apply
          </Button>
          <Button variant="outlined" onClick={handleResetFilter}>
            Reset
          </Button>
        </Grid>
      </Grid>

      <Table
        error={error}
        USERLIST={portfolios}
        page={page}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        TABLE_HEAD={TABLE_HEAD}
        pageInfo={pageInfo}
        loading={loading || nextLoader || prevLoader}
      />
    </Box>
  );
};

export default PortfolioTab;
